import React, { memo } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Theme,
  useMediaQuery,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import ArrowForward from '@material-ui/icons/ArrowForward';

import StatusBadge from '../../common/StatusBadge';
import EnvironmentBadge from '../../common/EnvironmentBadge';
import CopyIconButton from '../../common/CopyIconButton';
import ProjectInfoPopover from '../../common/ProjectInfoPopover';
import { AdminActionHint } from '../../common/AdminActionHint';
import { WarningIcon } from '../../icons/WarningIcon';
import LastRunStats from './LastRunStats';

import { useStyles } from '../../styles';
import '../../styles/projectCard.css';

import { getProjectStatusMessage } from '../../utils';
import {
  isCreateFailed,
  isCreateInProgress,
  isCreateLoginFailed,
  isProjectOperationInProgress,
} from '../../utils/projectStatusChecks';
import type { IqeProject } from '../../interfaces/ui';

interface ProjectCardProps {
  project: IqeProject;
  isToolAdmin: boolean;
  isGenerating: boolean;
  isRefreshingStatus: boolean;
  onRefreshStatus: (projectId: string) => void;
  isRerunning: boolean;
  onRerunValidation: (projectId: string) => void;
  onNavigateToExecution: (projectId: string) => void;
  onGenerateTests: (projectId: string) => void;
  onViewCurrentStatus: (
    projectId: string,
    projectName: string,
    transactionId: string,
    mode: 'generate' | 'execute' | 'enhance',
  ) => void;
  onRunTests: (projectId: string) => void;
  onRegenerateTests: (projectId: string) => void;
  onViewResults: (projectId: string) => void;
  onViewProjectDetails: (projectId: string) => void;
}

const ProjectCard = ({
  project,
  isToolAdmin,
  isGenerating,
  isRefreshingStatus,
  onRefreshStatus,
  isRerunning,
  onRerunValidation,
  onNavigateToExecution,
  onGenerateTests,
  onViewCurrentStatus,
  onRunTests,
  onRegenerateTests,
  onViewResults,
  onViewProjectDetails,
}: ProjectCardProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const isFailed =
    isCreateFailed(project.status) || isCreateLoginFailed(project.status);
  const isValidationFailed = isCreateLoginFailed(project.status);

  const isInProgress =
    isCreateInProgress(project.status) ||
    isProjectOperationInProgress(project.status);

  const isCurrentRunInProgress = isProjectOperationInProgress(project.status);

  const testPlanName =
    'planName' in project?.writeTestPlan
      ? project?.writeTestPlan?.planName
      : 'projectName' in project?.writeTestPlan
      ? project?.writeTestPlan?.projectName
      : '';
  const testSuiteName =
    'suiteName' in project?.writeTestPlan
      ? project?.writeTestPlan?.suiteName
      : 'subFolderName' in project?.writeTestPlan
      ? project?.writeTestPlan?.subFolderName
      : '';

  return (
    <Box className={`pc-card ${classes.projectCard}`}>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* TOP CONTENT ZONE: All content grouped tightly at top */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Box className="pc-content-zone">
        {/* Zone 1: Project name + badges (fixed height) */}
        <Box className="pc-zone-header">
          {/* Header Line 1: Project name and action icons */}
          <Box
            className={`pc-header-line1${isMobile ? ' pc-header-mobile' : ''}`}
          >
            <Typography variant="h6" className="pc-name">
              {project.name}
            </Typography>
            <Box className="pc-header-actions">
              {isInProgress && (
                <CardActionIconButton
                  title="Refresh status"
                  icon={
                    <RefreshIcon
                      className={
                        isRefreshingStatus ? 'pc-icon-spin' : undefined
                      }
                    />
                  }
                  onClick={() => onRefreshStatus(project.id)}
                  disabled={isRefreshingStatus}
                />
              )}
              <ProjectInfoPopover
                projectId={project.id}
                transactionId={project.transactionId}
              />
            </Box>
          </Box>

          {/* Header Line 2: Environment badge and status pill */}
          <Box className="pc-header-line2">
            <Box className="pc-badge-group">
              <EnvironmentBadge environment={project.environment} />
              <StatusBadge
                status={project.status}
                hasPreviousRuns={(project.runs?.length || 0) > 0}
              />
            </Box>
          </Box>
        </Box>

        {/* Zone 2: Fields row (fixed height — always shown, even if empty) */}
        <Box className="pc-zone-fields">
          <Box
            className={`pc-details-row${
              isMobile ? ' pc-details-row-mobile' : ''
            }`}
          >
            <Box className="pc-detail-item">
              <Typography component="span" className="pc-detail-label">
                Non-Prod App URL
              </Typography>
              <Typography
                component="span"
                className={`pc-detail-value${
                  isMobile ? ' pc-detail-url-value-mobile' : ''
                }`}
              >
                <CopyIconButton
                  dataToCopy={project.appUrl}
                  label="Copy URL"
                  size="small"
                  className="pc-copy-url-btn"
                  color="default"
                />
                <a
                  href={project.appUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {project.appUrl || '—'}
                </a>
              </Typography>
            </Box>
            <Box className="pc-detail-item">
              <Typography component="span" className="pc-detail-label">
                Test Plan Name
              </Typography>
              <Typography component="span" className="pc-detail-value">
                {testPlanName || '—'}
              </Typography>
            </Box>
            <Box className="pc-detail-item">
              <Typography component="span" className="pc-detail-label">
                Test Suite Name
              </Typography>
              <Typography component="span" className="pc-detail-value">
                {testSuiteName || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Other Failures State */}
        {isFailed && !isValidationFailed && (
          <>
            <Box className="pc-error-compact">
              <Box className="pc-error-icon-box">
                <WarningIcon />
              </Box>
              <Typography component="p" className="pc-error-text">
                Project creation failed due to an unexpected error. Please try
                again
              </Typography>
            </Box>

            <Box className="pc-next-steps-section">
              <Typography component="span" className="pc-section-label">
                Next Steps
              </Typography>
              <ol className="pc-next-steps-list">
                <li>Verify the Non-Prod App URL is correct and accessible</li>
                <li>
                  Verify credentials are valid and have proper permissions
                </li>
                {/* <li>Save any project changes</li> */}
                <li>Retry validation</li>
              </ol>
            </Box>
          </>
        )}

        {/* Zone 3: Stats row (fixed height — always shown, even if no runs) */}
        <LastRunStats
          latestRun={project.runs?.[0]}
          isCurrentRunInProgress={isCurrentRunInProgress}
          isMobile={isMobile}
        />

        {/* Zone 4: Bottom section with info bar and action button */}
        <Box
          className={`pc-zone-bottom${
            isFailed && !isToolAdmin ? ' pc-zone-bottom-admin-restricted' : ''
          }`}
        >
          {/* Info bar: 80% width */}
          <Box className="pc-zone-infobar">
            {isInProgress ? (
              <InfoIcon className="pc-infobar-icon" />
            ) : (
              <InfoIcon className="pc-infobar-icon" />
            )}
            {isValidationFailed ? (
              <Typography
                component="p"
                variant="body1"
                className="pc-empty-state-text"
              >
                {project.validationError ||
                  'Unable to validate project. Please check the Non-Prod App URL and credentials.'}
              </Typography>
            ) : (
              <Typography
                component="p"
                variant="body1"
                className="pc-empty-state-text"
              >
                {getProjectStatusMessage(project.status)}
              </Typography>
            )}
          </Box>

          {/* Action area: 20% width */}
          <Box className="pc-actions">
            {/* Failures: Retry Validation button */}
            {isFailed && (
              <Box className="pc-retry-action">
                <Button
                  variant="contained"
                  className="pc-retry-btn"
                  onClick={() => onRerunValidation(project.id)}
                  startIcon={
                    <RefreshIcon
                      className={isRerunning ? 'pc-icon-spin' : undefined}
                    />
                  }
                  disabled={!isToolAdmin || isRerunning}
                >
                  Retry Validation
                </Button>
                <AdminActionHint
                  isToolAdmin={isToolAdmin}
                  className="pc-retry-permission-hint"
                />
              </Box>
            )}

            {/* Ready State: Navigation arrow button */}
            {!isFailed && (
              <IconButton
                size="small"
                onClick={() => onNavigateToExecution(project.id)}
                className="pc-nav-arrow-btn"
                title="View Execution Centre"
              >
                <ArrowForward />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const IqeProjectCard = memo(ProjectCard);

interface CardActionIconButtonProps {
  title: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}

const CardActionIconButton = ({
  title,
  disabled,
  icon,
  onClick,
}: CardActionIconButtonProps) => (
  <IconButton
    size="small"
    title={title}
    onClick={onClick}
    disabled={disabled}
    className="pc-action-icon-btn"
  >
    {icon}
  </IconButton>
);
